import React from "react";

import { BundleGroupTypes, ConfiguratorTypes } from "../../../constants";
import { IAddon } from "../../../models/catalogue";
import { IProductID } from "../../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../reducers";
import { defaults } from "../defaults";
import { setSelectedUpgrade, updateSelectedAddons } from "../reducers";
import { baseVariantSelector } from "../selectors";
import { ConfiguratorUpsell } from "./ConfiguratorUpsell";
import { ConfiguratorUpsellContainer } from "./ConfiguratorUpsellContainer";
import { UpsellInfoModal } from "./ConfiguratorUpsellInfoModal";

export enum OptionPriceType {
    RELATIVE,
    ABSOLUTE,
    FREE_GIFT,
}

const handleUpgradeSelectionChange = (
    updates: IAddon[],
    setSelectedUpgrade: (productID: IProductID | null) => void,
): void => {
    if (updates.length > 0) {
        const update = updates[0];
        setSelectedUpgrade(update.quantity > 0 ? update.productID : null);
    }
};

export const ConfiguratorUpsells = (props: {
    configuratorType: ConfiguratorTypes;
}): React.ReactNode => {
    const dispatch = useAppDispatch();

    const baseVariant = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return baseVariantSelector(state);
    });

    const selectedUpgrade = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.ui.selectedUpgrade;
    });

    const selectedAddons = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.ui.selectedAddons;
    });

    const promoCards = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.entities.promoCards;
    });

    if (!baseVariant) {
        return null;
    }

    const selectedUpgradeFormatted = selectedUpgrade
        ? [
              {
                  productID: selectedUpgrade,
                  quantity: 1,
              },
          ]
        : [];

    return (
        <>
            {/* TODO(i18n): Translate HTML comment or use descriptive variable names instead: "Promo Cards" */}
            {/* Promo Cards */}
            {promoCards &&
                promoCards.map((promo, idx) => {
                    return (
                        <ConfiguratorUpsell
                            key={idx}
                            bundleID={null}
                            bundleType={BundleGroupTypes.PROMO_CARD}
                            sectionTitle={promo.section_title}
                            lineOne={promo.card_title}
                            lineTwo={promo.product_name}
                            lineThree={promo.description}
                            isSelected={false}
                            cardImage={promo.card_image_url}
                            flair={
                                promo.flair && promo.flair.length > 0
                                    ? promo.flair[0].value
                                    : null
                            }
                            infoModal={promo.info_modal}
                            configuratorType={props.configuratorType}
                        />
                    );
                })}
            {/* TODO(i18n): Translate HTML comment or use descriptive variable names instead: "Free Gifts" */}
            {/* Free Gifts */}
            <ConfiguratorUpsellContainer
                optionPriceType={OptionPriceType.FREE_GIFT}
                bundleType={BundleGroupTypes.IN_CONFIGURATOR_FREE_GIFT}
                selectedUpgrades={selectedAddons}
                onSelectionChange={(updates) =>
                    dispatch(updateSelectedAddons(updates))
                }
            />
            {/* TODO(i18n): Translate HTML comment or use descriptive variable names instead: "Product Upgrades" */}
            {/* Product Upgrades */}
            <ConfiguratorUpsellContainer
                optionPriceType={OptionPriceType.RELATIVE}
                bundleType={BundleGroupTypes.UPGRADE_PRODUCT}
                selectedUpgrades={selectedUpgradeFormatted}
                onSelectionChange={(updates) =>
                    handleUpgradeSelectionChange(updates, (productID) =>
                        dispatch(setSelectedUpgrade(productID)),
                    )
                }
            />
            {/* TODO(i18n): Translate HTML comment or use descriptive variable names instead: "Change Model" Upgrades */}
            {/* "Change Model" Upgrades */}
            <ConfiguratorUpsellContainer
                optionPriceType={OptionPriceType.RELATIVE}
                bundleType={BundleGroupTypes.UPGRADE_MODEL}
                selectedUpgrades={selectedUpgradeFormatted}
                onSelectionChange={(updates) =>
                    handleUpgradeSelectionChange(updates, (productID) =>
                        dispatch(setSelectedUpgrade(productID)),
                    )
                }
            />
            {/* TODO(i18n): Translate HTML comment or use descriptive variable names instead: "Product Add-Ons" */}
            {/* Product Add-Ons */}
            <ConfiguratorUpsellContainer
                optionPriceType={OptionPriceType.ABSOLUTE}
                bundleType={BundleGroupTypes.IN_CONFIGURATOR_ADD_ON}
                selectedUpgrades={selectedAddons}
                onSelectionChange={(updates) =>
                    dispatch(updateSelectedAddons(updates))
                }
            />
            <UpsellInfoModal
                configuratorType={props.configuratorType}
                selectedUpgrade={selectedUpgrade}
                selectedAddons={selectedAddons}
            />
        </>
    );
};
